import React from 'react';
import { tokens, type, CONTENT_MAX_WIDTH } from '../theme';
import { DEFAULT_SEASON } from '../config/seasons';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button
} from '@mui/material';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

const pages = [
  { name: 'Schedule', path: '/schedule' },
  { name: 'Standings', path: '/standings' },
  { name: 'Analytics', path: '/analytics' },
  { name: 'My Team', path: '/my-team' },
];

const Navbar = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const context = (() => {
    const path = location.pathname;
    const bits = (keys) => keys
      .map(k => searchParams.get(k) || (k === 'season' ? DEFAULT_SEASON : null))
      .filter(Boolean)
      .join(' · ');

    if (path === '/' || path === '/schedule') {
      return { title: 'Schedule', subtitle: bits(['age_group', 'season']) };
    }
    if (path === '/standings') {
      return { title: 'Standings', subtitle: bits(['age_group', 'season']) };
    }
    if (path === '/my-team') return { title: 'My Team' };
    if (path === '/analytics') {
      return { title: 'Quality of Play', subtitle: bits(['age_group', 'season']) };
    }
    if (path.startsWith('/team/')) return { title: 'Team' };
    if (path === '/admin') return { title: 'Admin' };
    return { title: 'League App' };
  })();


  const isActive = (path) => {
    if (path === '/schedule') {
      return location.pathname === '/' || location.pathname === '/schedule';
    }
    return location.pathname === path;
  };

  return (
    <AppBar position="static" elevation={0} sx={{ backgroundColor: tokens.navy }}>
      <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', width: '100%', px: { xs: 1.5, md: 3 } }}>
        <Toolbar disableGutters variant="dense" sx={{ minHeight: { xs: 48, md: 56 } }}>
          {/* Desktop Logo */}
          <SportsSoccerIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            League App
          </Typography>

          {/* Mobile: the active context, not the app name */}
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            flexGrow: 1,
            minWidth: 0,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}>
            <Typography noWrap sx={{
              fontSize: type.body,
              fontWeight: 700,
              color: 'inherit',
              lineHeight: 1.15,
            }}>
              {context?.title || 'League App'}
            </Typography>
            {context?.subtitle && (
              <Typography noWrap sx={{
                fontSize: type.label,
                color: 'rgba(255,255,255,0.68)',
                lineHeight: 1.25,
              }}>
                {context.subtitle}
              </Typography>
            )}
          </Box>

          {/* Desktop Menu */}
          {/* alignItems matters: without it this flex row defaults to stretch, so
              each Button's box grows to the full toolbar height. That alone would
              be harmless, but display:'block' below used to override the Button's
              own inline-flex centring, which left the label sitting at the top of
              a stretched highlight while the highlight itself looked centred
              against the brand. Centre the items and let the Button centre its
              own label. */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {pages.map((page) => (
              <Button
                key={page.name}
                component={RouterLink}
                to={page.path}
                sx={{
                  color: 'white',
                  backgroundColor: isActive(page.path) ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  }
                }}
              >
                {page.name}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </Box>
    </AppBar>
  );
};

export default Navbar;
